/**
 * MultiSelectBar - Multi-select action controls for the Overview page
 *
 * Shows cancel, mark done, archive, delete, and select all buttons
 * when multi-select mode is active.
 */

import {
  XMarkIcon,
  PlusIcon,
  MinusIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

// ---------- Helper functions ----------

/**
 * Compute button style based on selection state and loading
 */
function computeActionButtonStyle(hasSelection, isLoading, bgColor) {
  return {
    background: hasSelection ? bgColor : undefined,
    color: hasSelection ? "#000" : undefined,
    opacity: !hasSelection || isLoading ? 0.5 : 1,
  };
}

// ---------- Sub-components ----------

/**
 * Action button with selection-aware styling
 */
function ActionButton({
  className,
  onClick,
  disabled,
  isLoading,
  hasSelection,
  bgColor,
  loadingText,
  normalText,
  title,
}) {
  const style = computeActionButtonStyle(hasSelection, isLoading, bgColor);

  return (
    <button
      className={className}
      onClick={onClick}
      disabled={disabled}
      style={style}
      title={title}
    >
      {isLoading ? loadingText : normalText}
    </button>
  );
}

/**
 * Mark Done button
 */
function MarkDoneButton({ hasSelection, selectedCount, markingDone, onMarkDone }) {
  return (
    <ActionButton
      className="chip multi-done"
      onClick={onMarkDone}
      disabled={!hasSelection || markingDone}
      isLoading={markingDone}
      hasSelection={hasSelection}
      bgColor="var(--accent)"
      loadingText="Marking..."
      normalText={`Mark Done (${selectedCount})`}
    />
  );
}

/**
 * Archive button
 */
function ArchiveButton({ hasSelection, selectedCount, archivingPhotos, onArchive }) {
  return (
    <ActionButton
      className="chip multi-archive"
      onClick={onArchive}
      disabled={!hasSelection || archivingPhotos}
      isLoading={archivingPhotos}
      hasSelection={hasSelection}
      bgColor="#a29bff"
      loadingText="Archiving..."
      normalText={`Archive (${selectedCount})`}
    />
  );
}

/**
 * Delete button
 */
function DeleteButton({ hasSelection, selectedCount, deletingPhotos, onDelete }) {
  return (
    <ActionButton
      className="chip multi-delete"
      onClick={onDelete}
      disabled={!hasSelection || deletingPhotos}
      isLoading={deletingPhotos}
      hasSelection={hasSelection}
      bgColor="#ff6b6b"
      loadingText="Deleting..."
      normalText={`Delete (${selectedCount})`}
    />
  );
}

/**
 * Select All button
 */
function SelectAllButton({ effectiveTotal, selectingAll, hasMore, onSelectAll }) {
  const style = {
    background: "#66b7ff",
    color: "#000",
    opacity: effectiveTotal === 0 || selectingAll ? 0.5 : 1,
  };

  const title = hasMore
    ? `Select all ${effectiveTotal} photos matching current filters`
    : undefined;

  return (
    <button
      className="chip multi-select-all"
      onClick={onSelectAll}
      disabled={effectiveTotal === 0 || selectingAll}
      style={style}
      title={title}
    >
      <PlusIcon className="icon-sm" style={{ marginRight: 4 }} />
      {selectingAll ? "Selecting..." : `Select All (${effectiveTotal})`}
    </button>
  );
}

/**
 * Clear Selection button
 */
function ClearSelectionButton({ onClearSelection }) {
  return (
    <button
      className="chip multi-clear"
      onClick={onClearSelection}
      style={{ background: "#666", color: "#fff" }}
    >
      <MinusIcon className="icon-sm" style={{ marginRight: 4 }} /> Clear Selection
    </button>
  );
}

/**
 * Empty Environment button - destructive action, pushed to far right
 */
function EmptyEnvironmentButton({ onEmptyEnvironment }) {
  return (
    <button
      className="chip"
      onClick={onEmptyEnvironment}
      style={{
        marginLeft: 'auto',
        paddingLeft: 16,
        border: '1px solid #ff6b6b',
        background: 'transparent',
        color: '#ff6b6b',
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
      }}
      title="Delete all photos and reset the environment"
    >
      <ExclamationTriangleIcon className="icon-sm" />
      Empty Environment
    </button>
  );
}

// ---------- Main component ----------

function MultiSelectBar({
  selectedCount,
  loadedCount,
  totalCount,
  hasMore,
  onCancel,
  onMarkDone,
  onArchive,
  onDelete,
  onSelectAll,
  onClearSelection,
  markingDone,
  archivingPhotos,
  deletingPhotos,
  selectingAll,
  showArchive,
  // Permission-based visibility
  canMarkDone = true,
  canArchive = true,
  canDelete = true,
  canEmptyEnvironment = false,
  onEmptyEnvironment,
}) {
  const hasSelection = selectedCount > 0;
  const effectiveTotal = totalCount > 0 ? totalCount : loadedCount;
  const allSelected = selectedCount === effectiveTotal && effectiveTotal > 0;

  return (
    <>
      <button className="chip multi-cancel" onClick={onCancel}>
        <XMarkIcon className="icon-sm" style={{ marginRight: 4 }} /> Cancel
      </button>

      <span className="multi-count">
        {selectedCount} selected
        {hasMore && totalCount > loadedCount && (
          <span className="multi-loaded-hint"> ({loadedCount} loaded of {totalCount})</span>
        )}
      </span>

      {canMarkDone && (
        <MarkDoneButton
          hasSelection={hasSelection}
          selectedCount={selectedCount}
          markingDone={markingDone}
          onMarkDone={onMarkDone}
        />
      )}

      {canArchive && showArchive && (
        <ArchiveButton
          hasSelection={hasSelection}
          selectedCount={selectedCount}
          archivingPhotos={archivingPhotos}
          onArchive={onArchive}
        />
      )}

      {canDelete && (
        <DeleteButton
          hasSelection={hasSelection}
          selectedCount={selectedCount}
          deletingPhotos={deletingPhotos}
          onDelete={onDelete}
        />
      )}

      {!allSelected && (
        <SelectAllButton
          effectiveTotal={effectiveTotal}
          selectingAll={selectingAll}
          hasMore={hasMore}
          onSelectAll={onSelectAll}
        />
      )}

      {hasSelection && (
        <ClearSelectionButton onClearSelection={onClearSelection} />
      )}

      {canEmptyEnvironment && (
        <EmptyEnvironmentButton onEmptyEnvironment={onEmptyEnvironment} />
      )}
    </>
  );
}

export default MultiSelectBar;
